/* dh-r-quiz.js — DermaHabits DERM Check
 * Vanilla JS state machine for the dh-r-healing-check section.
 * - Reads quiz data + result definitions from <script type="application/json"> blocks
 * - States: intro -> questions -> email -> result (green / amber / red)
 * - Progressive enhancement on a real <form action="/contact" method="post"> (graceful no-JS)
 * - Fire-and-forget POST to /contact via fetch on submit (Mailchimp wiring lands tomorrow)
 * - Re-initializes on shopify:section:load for theme editor preview
 *
 * Note: rich-text strings injected here come from Liquid schema settings
 * (operator-controlled by Line via Shopify admin), not from end-user input.
 */
(function () {
  'use strict';

  function clearChildren(node) {
    while (node && node.firstChild) node.removeChild(node.firstChild);
  }

  function setRichText(node, html) {
    // Operator-controlled richtext from Shopify schema. Parsed via DOMParser
    // and re-attached as nodes (no innerHTML on live document tree).
    if (!node) return;
    clearChildren(node);
    if (!html) return;
    var doc = new DOMParser().parseFromString('<div>' + html + '</div>', 'text/html');
    var src = doc.body.firstChild;
    if (!src) return;
    while (src.firstChild) node.appendChild(src.firstChild);
  }

  function init(root) {
    if (!root || root.dataset.dhQuizReady === '1') return;
    root.dataset.dhQuizReady = '1';

    var dataEl = root.querySelector('script[data-dh-quiz]');
    var resultsEl = root.querySelector('script[data-dh-quiz-results]');
    if (!dataEl || !resultsEl) return;

    var quiz, results;
    try {
      quiz = JSON.parse(dataEl.textContent || '{}');
      results = JSON.parse(resultsEl.textContent || '{}');
    } catch (e) {
      return;
    }
    var questions = (quiz && quiz.questions) || [];
    var pills = (quiz && quiz.pills) || [];
    if (!questions.length) return;

    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* ---- Element refs ---- */
    var screens = {
      intro: root.querySelector('[data-screen="intro"]'),
      question: root.querySelector('[data-screen="question"]'),
      email: root.querySelector('[data-screen="email"]'),
      result: root.querySelector('[data-screen="result"]')
    };
    var stepLabel = root.querySelector('[data-step-label]');
    var barFill = root.querySelector('[data-bar-fill]');
    var pillsWrap = root.querySelector('[data-pills]');
    var live = root.querySelector('[data-live]');

    /* Question screen */
    var qNumEl = root.querySelector('[data-q-num]');
    var qBadgeEl = root.querySelector('[data-q-badge]');
    var qTextEl = root.querySelector('[data-q-text]');
    var qHintEl = root.querySelector('[data-q-hint]');
    var qOptsEl = root.querySelector('[data-q-opts]');
    var btnBack = root.querySelector('[data-btn-back]');
    var btnNext = root.querySelector('[data-btn-next]');
    var btnStart = root.querySelector('[data-btn-start]');

    /* Email screen */
    var form = root.querySelector('[data-quiz-form]');
    var firstInput = root.querySelector('[data-input-first]');
    var lastInput = root.querySelector('[data-input-last]');
    var emailInput = root.querySelector('[data-input-email]');
    var emailErr = root.querySelector('[data-email-error]');
    var hiddenTags = root.querySelector('[data-hidden-tags]');

    /* Result screen */
    var rBadge = root.querySelector('[data-result-badge]');
    var rTitle = root.querySelector('[data-result-title]');
    var rBody = root.querySelector('[data-result-body]');
    var rScores = root.querySelector('[data-result-scores]');
    var rPrimary = root.querySelector('[data-result-primary]');
    var rSecondary = root.querySelector('[data-result-secondary]');

    /* ---- State ---- */
    var current = 0;
    var answers = new Array(questions.length).fill(null);

    /* ---- Helpers ---- */
    function showScreen(name) {
      Object.keys(screens).forEach(function (k) {
        var el = screens[k];
        if (!el) return;
        if (k === name) {
          el.dataset.active = 'true';
        } else {
          delete el.dataset.active;
        }
      });
    }

    function announce(msg) {
      if (live) live.textContent = msg;
    }

    function setProgress(pct, label) {
      if (barFill) barFill.style.width = pct + '%';
      if (stepLabel) stepLabel.textContent = label;
    }

    function highlightPill(catName) {
      if (!pillsWrap) return;
      var nodes = pillsWrap.querySelectorAll('[data-pill]');
      nodes.forEach(function (n) {
        if (catName && n.dataset.pill === catName) {
          n.dataset.on = 'true';
        } else {
          delete n.dataset.on;
        }
      });
    }

    function clearPills() {
      if (!pillsWrap) return;
      pillsWrap.querySelectorAll('[data-pill]').forEach(function (n) { delete n.dataset.on; });
    }

    function buildPills() {
      if (!pillsWrap || !pills.length) return;
      clearChildren(pillsWrap);
      pills.forEach(function (name) {
        var p = document.createElement('span');
        p.className = 'dh-r-hc__pill';
        p.dataset.pill = name;
        p.textContent = name;
        pillsWrap.appendChild(p);
      });
    }

    function badgeIndex(category) {
      var idx = pills.indexOf(category);
      if (idx < 0) idx = 0;
      return idx % 5;
    }

    /* ---- Render question ---- */
    function renderQuestion() {
      var q = questions[current];
      if (!q) return;

      var label = 'Spørgsmål ' + (current + 1) + ' af ' + questions.length;
      setProgress(Math.round((current / questions.length) * 100), label);
      highlightPill(q.category);

      if (qNumEl) qNumEl.textContent = label;
      if (qBadgeEl) {
        qBadgeEl.textContent = q.category || '';
        qBadgeEl.className = 'dh-r-hc__q-badge dh-r-hc__q-badge--b' + badgeIndex(q.category);
      }
      if (qTextEl) qTextEl.textContent = q.question || '';
      if (qHintEl) {
        qHintEl.textContent = q.hint || '';
        qHintEl.style.display = q.hint ? '' : 'none';
      }

      if (qOptsEl) {
        clearChildren(qOptsEl);
        (q.options || []).forEach(function (opt, i) {
          var d = document.createElement('button');
          d.type = 'button';
          d.className = 'dh-r-hc__opt';
          d.setAttribute('role', 'radio');
          d.setAttribute('aria-checked', answers[current] === i ? 'true' : 'false');
          if (answers[current] === i) d.dataset.sel = 'true';

          var radio = document.createElement('span');
          radio.className = 'dh-r-hc__radio';
          radio.setAttribute('aria-hidden', 'true');
          var rdot = document.createElement('span');
          rdot.className = 'dh-r-hc__rdot';
          radio.appendChild(rdot);

          var txt = document.createElement('span');
          txt.className = 'dh-r-hc__opt-text';
          txt.textContent = opt.text || '';

          d.appendChild(radio);
          d.appendChild(txt);

          d.addEventListener('click', function () { selectOption(i, d); });
          d.addEventListener('keydown', function (ev) {
            if (ev.key === ' ' || ev.key === 'Enter') {
              ev.preventDefault();
              selectOption(i, d);
            }
          });
          qOptsEl.appendChild(d);
        });
      }

      if (btnBack) btnBack.style.visibility = current === 0 ? 'hidden' : 'visible';
      if (btnNext) {
        btnNext.disabled = answers[current] === null;
        btnNext.textContent = current === questions.length - 1 ? 'Se mit resultat →' : 'Næste →';
      }

      announce(label + ', kategori ' + (q.category || ''));
    }

    function selectOption(i, el) {
      answers[current] = i;
      var nodes = qOptsEl.querySelectorAll('[role="radio"]');
      nodes.forEach(function (n) {
        delete n.dataset.sel;
        n.setAttribute('aria-checked', 'false');
      });
      el.dataset.sel = 'true';
      el.setAttribute('aria-checked', 'true');
      if (btnNext) btnNext.disabled = false;
    }

    /* ---- Flow ---- */
    function startQuiz() {
      current = 0;
      showScreen('question');
      renderQuestion();
    }

    function goNext() {
      if (answers[current] === null) return;
      if (current < questions.length - 1) {
        current++;
        renderQuestion();
      } else {
        showScreen('email');
        setProgress(95, 'Næsten der');
        clearPills();
        announce('Email-trin. Indtast dine oplysninger for at se dit resultat.');
        if (firstInput) firstInput.focus();
      }
    }

    function goBack() {
      if (current > 0) {
        current--;
        renderQuestion();
      }
    }

    /* ---- Score calc ---- */
    function calcScores() {
      var totalScore = 0;
      var totalMax = 0;
      var catSum = {};
      var catMax = {};

      questions.forEach(function (q, i) {
        var opts = q.options || [];
        var maxScore = 0;
        opts.forEach(function (o) { if ((o.score || 0) > maxScore) maxScore = o.score || 0; });
        if (maxScore < 1) maxScore = 1;
        var got = (answers[i] !== null && opts[answers[i]]) ? (opts[answers[i]].score || 0) : 0;
        totalScore += got;
        totalMax += maxScore;
        var cat = q.category || 'Andet';
        catSum[cat] = (catSum[cat] || 0) + got;
        catMax[cat] = (catMax[cat] || 0) + maxScore;
      });

      var pct = totalMax > 0 ? totalScore / totalMax : 0;
      var level = pct < 0.33 ? 'green' : (pct < 0.60 ? 'amber' : 'red');

      var catPcts = {};
      Object.keys(catSum).forEach(function (k) {
        catPcts[k] = catMax[k] > 0 ? catSum[k] / catMax[k] : 0;
      });

      return { level: level, pct: pct, scores: catPcts, totalScore: totalScore, totalMax: totalMax };
    }

    function levelLabel(p) {
      return p < 0.33 ? 'God balance' : (p < 0.60 ? 'Hviskende' : 'Råber');
    }
    function levelClass(p) {
      return p < 0.33 ? 'green' : (p < 0.60 ? 'amber' : 'red');
    }

    /* ---- Render result ---- */
    function renderResult(result) {
      var def = results[result.level] || {};
      showScreen('result');
      setProgress(100, 'Dit resultat');
      clearPills();

      if (rBadge) {
        rBadge.textContent = def.badge || '';
        rBadge.className = 'dh-r-hc__result-badge dh-r-hc__result-badge--' + result.level;
      }
      if (rTitle) rTitle.textContent = def.title || '';
      setRichText(rBody, def.body || '');

      if (rScores) {
        clearChildren(rScores);
        var head = document.createElement('p');
        head.className = 'dh-r-hc__scores-eyebrow';
        head.textContent = 'Din profil';
        rScores.appendChild(head);

        pills.forEach(function (cat) {
          var p = result.scores[cat];
          if (p === undefined || p === null) return;
          var pct = Math.round(p * 100);
          var cls = levelClass(p);
          var lbl = levelLabel(p);

          var row = document.createElement('div');
          row.className = 'dh-r-hc__score-row';

          var labelRow = document.createElement('div');
          labelRow.className = 'dh-r-hc__score-label-row';
          var catEl = document.createElement('span');
          catEl.className = 'dh-r-hc__score-cat';
          catEl.textContent = cat;
          var valEl = document.createElement('span');
          valEl.className = 'dh-r-hc__score-val dh-r-hc__score-val--' + cls;
          valEl.textContent = lbl;
          labelRow.appendChild(catEl);
          labelRow.appendChild(valEl);

          var track = document.createElement('div');
          track.className = 'dh-r-hc__score-track';
          var fill = document.createElement('div');
          fill.className = 'dh-r-hc__score-fill dh-r-hc__score-fill--' + cls;
          fill.style.width = (reduceMotion ? pct : 0) + '%';
          track.appendChild(fill);

          row.appendChild(labelRow);
          row.appendChild(track);
          rScores.appendChild(row);

          if (!reduceMotion) {
            requestAnimationFrame(function () {
              setTimeout(function () { fill.style.width = pct + '%'; }, 50);
            });
          }
        });
      }

      if (rPrimary) {
        rPrimary.textContent = def.primary_label || '';
        rPrimary.href = def.primary_url || '#';
        rPrimary.style.display = def.primary_label ? '' : 'none';
      }
      if (rSecondary) {
        if (def.secondary_label && def.secondary_url) {
          rSecondary.textContent = def.secondary_label;
          rSecondary.href = def.secondary_url;
          rSecondary.style.display = '';
        } else {
          rSecondary.style.display = 'none';
        }
      }

      announce('Dit resultat er klar: ' + (def.title || ''));

      try {
        sessionStorage.setItem('dh-r-hc-last', JSON.stringify({
          ts: Date.now(),
          level: result.level,
          scores: result.scores
        }));
      } catch (e) { /* noop */ }
    }

    /* ---- Submit ---- */
    function isValidEmail(v) {
      return !!v && v.indexOf('@') > 0 && v.indexOf('.') > 0 && /\S+@\S+\.\S+/.test(v);
    }

    /* ---- Klaviyo (DERM Check → liste + event) ---- */
    function sendToKlaviyo(email, first, last, result) {
      var company = (root.dataset.klaviyoCompany || '').trim();
      var listId = (root.dataset.klaviyoList || '').trim();
      if (!company || typeof fetch !== 'function') return;

      // GDPR: kun resultat/segment til Klaviyo — INGEN helbredsscorer eller svar.
      // Scorerne beregnes stadig lokalt og vises kun i resultatskærmen.
      var props = {
        healing_check_result: result.level
      };

      var profileAttrs = { email: email, properties: props };
      if (first) profileAttrs.first_name = first;
      if (last) profileAttrs.last_name = last;

      var headers = { 'Content-Type': 'application/json', revision: '2024-10-15' };

      // 1) Tilmeld personen til Klaviyo-listen (med samtykke)
      if (listId) {
        try {
          fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
            method: 'POST', headers: headers,
            body: JSON.stringify({
              data: {
                type: 'subscription',
                attributes: {
                  custom_source: 'DERM Check',
                  profile: { data: { type: 'profile', attributes: profileAttrs } }
                },
                relationships: { list: { data: { type: 'list', id: listId } } }
              }
            })
          }).catch(function () {});
        } catch (e) { /* noop */ }
      }

      // 2) Send "Completed DERM Check"-event med resultat + scores
      try {
        fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(company), {
          method: 'POST', headers: headers,
          body: JSON.stringify({
            data: {
              type: 'event',
              attributes: {
                metric: { data: { type: 'metric', attributes: { name: 'Completed DERM Check' } } },
                properties: props,
                profile: { data: { type: 'profile', attributes: { email: email, first_name: first, last_name: last } } }
              }
            }
          })
        }).catch(function () {});
      } catch (e) { /* noop */ }
    }

    function handleSubmit(ev) {
      if (ev) ev.preventDefault();

      var email = (emailInput && emailInput.value || '').trim();
      var first = (firstInput && firstInput.value || '').trim();
      var last = (lastInput && lastInput.value || '').trim();

      if (!isValidEmail(email)) {
        if (emailErr) {
          emailErr.textContent = 'Indtast venligst en gyldig email.';
          emailErr.dataset.show = 'true';
        }
        if (emailInput) {
          emailInput.dataset.invalid = 'true';
          emailInput.focus();
        }
        return;
      }
      if (emailErr) { emailErr.textContent = ''; delete emailErr.dataset.show; }
      if (emailInput) delete emailInput.dataset.invalid;

      var result = calcScores();

      // GDPR: /contact må ikke bære svar, scorer eller painpoint.
      // Kun et neutralt tag, ingen note.
      if (hiddenTags) hiddenTags.value = 'healing-check';

      // Fire-and-forget POST to /contact (graceful — works without it)
      try {
        if (form && typeof FormData === 'function' && typeof fetch === 'function') {
          var fd = new FormData(form);
          fetch('/contact', { method: 'POST', body: fd, credentials: 'same-origin' })
            .catch(function () { /* swallow — Mailchimp wiring tomorrow */ });
        }
      } catch (e) { /* noop */ }

      // Send leadet til Klaviyo (tilmeld liste + resultat-event) — påvirker ikke resultatet
      try { sendToKlaviyo(email, first, last, result); } catch (e) { /* noop */ }

      renderResult(result);
    }

    /* ---- Wire it up ---- */
    buildPills();
    showScreen('intro');
    setProgress(0, 'Start');

    if (btnStart) btnStart.addEventListener('click', startQuiz);
    if (btnNext) btnNext.addEventListener('click', goNext);
    if (btnBack) btnBack.addEventListener('click', goBack);
    if (form) form.addEventListener('submit', handleSubmit);

    // Enter on email field submits
    [firstInput, lastInput, emailInput].forEach(function (inp) {
      if (!inp) return;
      inp.addEventListener('keydown', function (ev) {
        if (ev.key === 'Enter') {
          ev.preventDefault();
          handleSubmit(ev);
        }
      });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-dh-r-hc]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-dh-r-hc]');
    if (section) {
      delete section.dataset.dhQuizReady;
      init(section);
    }
  });
})();
